import { isArray } from "lodash";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { TopHeaderCalendar } from "@ryde/controls";
import { PageHeader } from "@ryde/layout";
import {
  IPageHeaderContext,
  PageHeaderContext,
  usePageContext,
  useReportActions,
  useReportId,
} from "@ryde/redux";
import {
  Log,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  reportsChartDrawerAtom,
  reportsNavigationDrawerAtom,
  reportsOperationsDrawerAtom,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import { ReportsRelatedEntitiesContentBulk } from "./bulk";
import {
  ReportChartDrawer,
  ReportNavigationDrawer,
  ReportOperationsDrawer,
  TableSettingsDrawerReports,
} from "./drawers";
import { useTableDefsReports } from "./grid";
import { PageHeaderFiltersReport, PageHeaderMobileReports } from "./header";
import { ReportsPanelTopStatLine } from "./parts";
import { ReportsSwitchRoutes } from "./routes";

export const ReportsPageContent: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { fetchReport } = useReportActions();
  const { reportId } = useReportId();
  const { tableDefsReports } = useTableDefsReports();
  const periodGrouping = useRecoilValue(reportGroupingPeriodAF(reportId));

  const groupingParams = useRecoilValue(reportFlexGroupingParamsAF(reportId));

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const reportsOperationsDrawerOpened = useRecoilValue(
    reportsOperationsDrawerAtom,
  );
  const reportsNavigationDrawerOpened = useRecoilValue(
    reportsNavigationDrawerAtom,
  );
  const reportsChartDrawerOpened = useRecoilValue(reportsChartDrawerAtom);

  const serializedGroupingParams = useMemo(() => {
    if (!groupingParams?.length || !isArray(groupingParams)) {
      return "";
    }

    return groupingParams.join(",");
  }, [groupingParams]);

  const initialSearchParamsMemoised = useMemo(() => {
    return {
      date1: calendarDates?.date1,
      date2: calendarDates?.date2,
      periodGrouping,
      serializedGroupingParams,
    };
  }, [
    calendarDates?.date1,
    calendarDates?.date2,
    periodGrouping,
    serializedGroupingParams,
  ]);

  useEffect(() => {
    const abortController = new AbortController();

    if (
      initialSearchParamsMemoised?.date1 &&
      initialSearchParamsMemoised?.date2 &&
      reportId
    ) {
      Log.debug(
        `FETCH REPORTS CALLED, for dates: ${initialSearchParamsMemoised.date1}-${initialSearchParamsMemoised.date1}`,
      );

      fetchReport({ abortController });
    }

    return () => {
      abortController.abort();
      Log.debug("Fetching reports aborted on page unmount");
    };
  }, [initialSearchParamsMemoised, fetchReport, reportId]);

  const onFiltersApply = useEvent((): void => {
    fetchReport({});
  });

  const reportsHeaderContextValue: IPageHeaderContext = useMemo(() => {
    return {
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  return (
    <>
      <PageHeader
        mainTitle={t("general.REPORTS")}
        topWidgetComponent={<TopHeaderCalendar entity={entity} />}
        bottomWidgetComponent={<ReportsPanelTopStatLine />}
        customRightComponent={null}
        CustomMobileHeaderComponent={PageHeaderMobileReports}
      >
        <PageHeaderContext.Provider value={reportsHeaderContextValue}>
          <PageHeaderFiltersReport />
        </PageHeaderContext.Provider>
      </PageHeader>
      <ReportsSwitchRoutes />
      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerReports tableDefs={tableDefsReports || []} />
      )}
      {reportsOperationsDrawerOpened && <ReportOperationsDrawer />}
      {reportsNavigationDrawerOpened && <ReportNavigationDrawer />}
      {reportsChartDrawerOpened && <ReportChartDrawer />}
      <ReportsRelatedEntitiesContentBulk />
    </>
  );
};
